import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/firebaseInit';
import {
  coll_availabilitySlots,
  normaliseAvailabilitySlot,
  type AvailabilitySlotDraftType,
  type AvailabilitySlotType,
} from '@/types/types_availabilitySlot';

// Data access for the availability calendar of 'direct' experiences.
//
// Creating and deleting a slot happen here, from the browser, because a slot is
// content: an admin typing up what the artisan has agreed to work. The security
// rules allow exactly that much and no more — see firestore.rules.
//
// What is *not* here is booking one. Taking a slot has to check it is still free
// and write the booking in the same breath, so it lives in the
// `createBookingRequest` transaction; releasing one is a cancellation, and lives
// in `setBookingRequestStatus`. A slot's `status` is therefore never written from
// this file, and could not be — the rules refuse it.

const slotsCollection = collection(db, coll_availabilitySlots);

function toSlot(id: string, data: Record<string, unknown>): AvailabilitySlotType {
  return normaliseAvailabilitySlot(data as Partial<AvailabilitySlotType>, id);
}

/**
 * Subscribes to one experience's whole calendar, in calendar order.
 *
 * Unfiltered by status and by date, which is deliberate on both counts: the CMS
 * shows booked slots alongside open ones (a calendar with the taken days missing
 * is unreadable), and past slots are the record of what has been run. One
 * experience's diary is small, so paring it down is the caller's job — see
 * `isSlotBookable`.
 *
 * Live rather than fetched once, and for the same reason the booking queue is: a
 * slot can be taken by another partner while this page is open, and the honest
 * thing is for it to disappear from the picker rather than fail on submit.
 */
export function subscribeSlotsForExperience(
  experienceId: string,
  onChange: (slots: AvailabilitySlotType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(
      slotsCollection,
      where('experienceId', '==', experienceId),
      orderBy('date'),
      orderBy('startTime'),
    ),
    (snap) => onChange(snap.docs.map((d) => toSlot(d.id, d.data()))),
    onError,
  );
}

/**
 * Adds a slot to an experience's calendar. Admins only, per the rules.
 *
 * The id is Firestore's rather than derived from the date and time, so that
 * deleting a slot and adding the same hour back is a new slot with no history of
 * the old one attached — a booking that pointed at the deleted one must not
 * appear to point at its replacement.
 */
export async function createAvailabilitySlot(
  experienceId: string,
  draft: AvailabilitySlotDraftType,
  createdByUid: string,
): Promise<void> {
  await addDoc(slotsCollection, {
    experienceId,
    date: draft.date,
    startTime: draft.startTime,
    endTime: draft.endTime,
    // Stated rather than left to a default: the rules require it, and it is what
    // says this document has never been booked.
    status: 'open',
    createdAt: new Date().toISOString(),
    createdByUid,
  });
}

/**
 * Firestore takes at most 500 writes in a batch. Well under it, because the
 * limit counts writes rather than documents and there is no reason to sit on the
 * edge of it.
 */
const maxSlotsPerBatch = 400;

/**
 * Adds a run of slots — a month of an artisan's usual working day, typed once.
 *
 * Batched so that a fortnight of calendar is a handful of round trips rather
 * than a few hundred, and committed in order so that a failure part way through
 * leaves whole days written rather than a scattering. That is deliberately not
 * all-or-nothing: the slots are independent documents, each one perfectly valid
 * on its own, and the caller re-plans against what is now on the calendar — so
 * running it again fills in whatever the failure left out.
 *
 * The drafts must already have been checked (see `planBulkSlots`): nothing here
 * looks at closures, clashes or the clock.
 */
export async function createAvailabilitySlots(
  experienceId: string,
  drafts: AvailabilitySlotDraftType[],
  createdByUid: string,
): Promise<void> {
  const createdAt = new Date().toISOString();
  for (let i = 0; i < drafts.length; i += maxSlotsPerBatch) {
    const batch = writeBatch(db);
    for (const draft of drafts.slice(i, i + maxSlotsPerBatch)) {
      batch.set(doc(slotsCollection), {
        experienceId,
        date: draft.date,
        startTime: draft.startTime,
        endTime: draft.endTime,
        status: 'open',
        createdAt,
        createdByUid,
      });
    }
    await batch.commit();
  }
}

/**
 * Removes an open slot from the calendar — the artisan has taken the morning
 * back, or it was typed in wrong.
 *
 * A booked slot cannot be deleted, and the rules are what enforce that rather
 * than this function: it holds a booking a partner has been told about, and losing
 * the document would leave that booking pointing at nothing. Cancel the booking
 * instead, which releases the slot properly.
 */
export async function deleteAvailabilitySlot(slotId: string): Promise<void> {
  await deleteDoc(doc(slotsCollection, slotId));
}
